import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

interface ImpersonationState {
  isImpersonating: boolean;
  originalAdminId: string | null;
  impersonatedUserId: string | null;
  impersonatedUserEmail: string | null;
}

const IMPERSONATION_KEY = 'impersonation_state';

export const useImpersonation = () => {
  const { user } = useAuth();
  const [impersonationState, setImpersonationState] = useState<ImpersonationState>(() => {
    // Charger l'état depuis localStorage au démarrage
    const stored = localStorage.getItem(IMPERSONATION_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return {
          isImpersonating: false,
          originalAdminId: null,
          impersonatedUserId: null,
          impersonatedUserEmail: null,
        };
      }
    }
    return {
      isImpersonating: false,
      originalAdminId: null,
      impersonatedUserId: null,
      impersonatedUserEmail: null,
    };
  });

  // Sauvegarder dans localStorage à chaque changement
  useEffect(() => {
    if (impersonationState.isImpersonating) {
      localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(impersonationState));
    } else {
      localStorage.removeItem(IMPERSONATION_KEY);
    }
  }, [impersonationState]);

  // Vérifier si l'utilisateur actuel est super admin
  const checkIsSuperAdmin = useCallback(async (userId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error checking super admin status:', error);
        return false;
      }

      return data?.role === 'super_admin';
    } catch (error) {
      console.error('Exception checking super admin:', error);
      return false;
    }
  }, []);

  // Logger l'action d'impersonation
  const logImpersonation = useCallback(async (
    adminId: string,
    targetUserId: string,
    action: 'start' | 'stop'
  ) => {
    try {
      await supabase.from('impersonation_logs').insert({
        admin_id: adminId,
        target_user_id: targetUserId,
        action: action,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Error logging impersonation:', error);
    }
  }, []);

  // Démarrer l'impersonation (mode vue)
  const startImpersonation = useCallback(async (targetUserId: string, targetUserEmail: string) => {
    if (!user) {
      throw new Error('Vous devez être connecté');
    }

    // Ne pas permettre l'impersonation si déjà en cours
    if (impersonationState.isImpersonating) {
      throw new Error('Vous êtes déjà en mode impersonation');
    }

    // Vérifier que l'utilisateur actuel est super admin
    const isSuperAdmin = await checkIsSuperAdmin(user.id);
    if (!isSuperAdmin) {
      throw new Error('Seuls les super admins peuvent utiliser l\'impersonation');
    }

    // Logger l'action
    await logImpersonation(user.id, targetUserId, 'start');

    // Mettre à jour l'état
    const newState: ImpersonationState = {
      isImpersonating: true,
      originalAdminId: user.id,
      impersonatedUserId: targetUserId,
      impersonatedUserEmail: targetUserEmail,
    };
    setImpersonationState(newState);

    // Recharger la page pour appliquer l'impersonation
    window.location.href = '/';
  }, [user, impersonationState.isImpersonating, logImpersonation]);

  // Arrêter l'impersonation
  const stopImpersonation = useCallback(async () => {
    if (!impersonationState.isImpersonating || !impersonationState.originalAdminId) {
      return;
    }

    // Logger l'action
    await logImpersonation(
      impersonationState.originalAdminId,
      impersonationState.impersonatedUserId!,
      'stop'
    );

    // Réinitialiser l'état d'impersonation
    setImpersonationState({
      isImpersonating: false,
      originalAdminId: null,
      impersonatedUserId: null,
      impersonatedUserEmail: null,
    });

    // Nettoyer le localStorage
    localStorage.removeItem(IMPERSONATION_KEY);

    // Recharger la page pour revenir à l'admin
    window.location.href = '/admin';
  }, [impersonationState, logImpersonation]);

  // Obtenir l'ID utilisateur effectif (impersonated ou réel)
  // À utiliser dans toutes les requêtes pour afficher les données de l'utilisateur cible
  const getEffectiveUserId = useCallback((): string | null => {
    if (impersonationState.isImpersonating && impersonationState.impersonatedUserId) {
      return impersonationState.impersonatedUserId;
    }
    return user?.id || null;
  }, [impersonationState, user]);

  return {
    isImpersonating: impersonationState.isImpersonating,
    originalAdminId: impersonationState.originalAdminId,
    impersonatedUserId: impersonationState.impersonatedUserId,
    impersonatedUserEmail: impersonationState.impersonatedUserEmail,
    startImpersonation,
    stopImpersonation,
    getEffectiveUserId,
  };
};
